import { useMemo, useState } from 'react'
import { Banner, Button, Empty, Field, Row } from '../ui'
import { ScreenHead } from '../components/ScreenHead'
import { useEntryState, useFeed } from '../lib/queries'
import { useForgetEntry } from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { iso } from '../lib/calendar'
import { sortDate } from '../lib/entries'
import { rowTitle, metaLine } from '../lib/rowMeta'
import { hiddenRows, matches } from '../lib/browse'
import type { FeedRow } from '../lib/types'
import { Loading, Failed } from './Stub'

/** One identity for "nothing yet". */
const NONE_YET: never[] = []

const monthKey = (day: string) => day.slice(0, 7) // 'YYYY-MM'

const monthLabel = (key: string) =>
  new Date(`${key}-01T12:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' })

/**
 * F7's "Bring them back" made a list: every dismissed row, restorable one at
 * a time rather than all at once — a swipe you can undo individually is what
 * F7 asked for in place of the one bulk button `Feed.tsx` used to carry.
 *
 * A dismissal is a delete on `entry_state`, so "restore" is `useForgetEntry`
 * — the same mutation that already undoes the toast's own "Undo" on
 * `EventRow`. No new write exists here; this screen is only a second way to
 * reach the one that does.
 *
 * Built on `ui/Row` directly rather than `EventRow`, which the brief says not
 * to touch and which has no "Restore" trailing slot to offer — a dismissed
 * row wants exactly one action, not the heart and the swipe gestures a live
 * row carries.
 */
export function Hidden({ onBack }: { onBack: () => void }) {
  const [q, setQ] = useState('')
  const feedQ = useFeed()
  const stateQ = useEntryState()
  const forgetM = useForgetEntry()
  const writeError = useWriteError(forgetM)

  const feed = feedQ.data ?? NONE_YET
  const stateRows = stateQ.data ?? NONE_YET

  const { groups, undated, past } = useMemo(() => {
    const today = iso(new Date())

    // A dismissed id can have dropped out of `feed` entirely since — its run
    // ended, or it was cancelled. Those rows are gone from the database and
    // there is nothing left here to restore, so they are silently absent
    // rather than counted against a total nobody can act on (the same call
    // `Saved.tsx` makes for its own `expired` count, stated rather than
    // hidden there because that screen is about what you kept; this one is
    // about what you can still bring back). `hiddenRows` is also what
    // Browse's "Hidden · N" counts, so the link and the page agree (a1-06).
    const rows = hiddenRows(stateRows, feed).filter(e => matches(e, q))

    const end = (e: FeedRow) => e.run_end ?? e.run_start ?? e.published_on
    const dated = rows.filter(e => !!end(e))
    const undated = rows.filter(e => !end(e))

    const isPast = (e: FeedRow) => end(e)! < today
    const past = dated.filter(isPast).sort((a, b) => (sortDate(a) > sortDate(b) ? -1 : 1))
    const current = dated.filter(e => !isPast(e)).sort((a, b) => (sortDate(a) < sortDate(b) ? -1 : 1))

    const byMonth = new Map<string, typeof rows>()
    for (const e of current) {
      const key = monthKey(sortDate(e))
      const list = byMonth.get(key)
      if (list) list.push(e); else byMonth.set(key, [e])
    }
    const groups = [...byMonth.keys()].sort().map(key => ({ key, label: monthLabel(key), rows: byMonth.get(key)! }))

    return { groups, undated, past }
  }, [feed, stateRows, q])

  const pending = feedQ.isPending || stateQ.isPending
  const error = feedQ.error ?? stateQ.error
  // WP 10.7: this screen had no `ScreenHead` at all — 10.1 moved the account
  // button out of the wordmark bar into each tab's own head, and this route
  // (reached from Browse, not a tab) never got one.
  if (pending) return <><ScreenHead lead={<h1 className="screen-title">Hidden</h1>} /><Loading /></>
  if (error) return <><ScreenHead lead={<h1 className="screen-title">Hidden</h1>} /><Failed error={error.message} /></>

  const total = groups.reduce((n, g) => n + g.rows.length, 0) + undated.length + past.length

  const row = (e: FeedRow) => {
    const m = metaLine(e, { now: new Date() })
    const rest = [m.venue, m.fact].filter(Boolean).join(' · ')
    return (
      <Row
        key={e.id}
        className="ev-row"
        leading={null}
        title={rowTitle(e)}
        meta={(
          <>
            <span className="ev-row__dot" style={{ background: m.dot }} aria-hidden="true" />
            <b className="ev-row__kind">{m.kind}</b>
            <b className="ev-row__when">{m.when}</b>
            {rest && <span className="ev-row__rest">{rest}</span>}
          </>
        )}
        trailing={
          <Button size="sm" variant="quiet" onClick={() => forgetM.mutate({ ids: [e.id] })}>
            Restore
          </Button>
        }
      />
    )
  }

  return (
    <>
      <Button variant="quiet" onClick={onBack}>← Browse</Button>
      <ScreenHead lead={<h1 className="screen-title">Hidden</h1>} />
      {writeError && <Banner tone="bad">{writeError}</Banner>}

      <Field.Input
        type="search" value={q} onChange={ev => setQ(ev.currentTarget.value)}
        aria-label="Search hidden events" placeholder="Search hidden events"
      />

      {total === 0 && <Empty title={q ? `Nothing matching “${q}”.` : 'Nothing hidden.'} />}

      {groups.map(g => (
        <section key={g.key}>
          <div className="rowhead"><h2>{g.label}</h2><span className="g">{g.rows.length}</span></div>
          {g.rows.map(row)}
        </section>
      ))}

      {undated.length > 0 && (
        <section>
          <div className="rowhead"><h2>No date given</h2><span className="g">{undated.length}</span></div>
          {undated.map(row)}
        </section>
      )}

      {past.length > 0 && (
        <section>
          <div className="rowhead"><h2>Past</h2><span className="g">{past.length}</span></div>
          {past.map(row)}
        </section>
      )}
    </>
  )
}
